import React, { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import TopNav from "@/components/TopNav";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, ArrowRight, Check, MapPin } from "lucide-react";
import { getPillar, US_STATES } from "@/data/pillars";

export const SERVICES_TESTIDS = {
    pillarHeader: "services-pillar-header",
    serviceOption: "services-service-option",
    stateOption: "services-state-option",
    continueBtn: "services-continue-btn",
    step1: "services-step-1",
    step2: "services-step-2",
};

const COLOR_MAP = {
    emerald: {
        badge: "bg-emerald-50 text-emerald-800 border-emerald-100",
        accent: "text-emerald-700",
        button: "bg-emerald-700 hover:bg-emerald-800",
        chip: "border-emerald-200 hover:border-emerald-500 hover:bg-emerald-50 data-[active=true]:bg-emerald-700 data-[active=true]:text-white data-[active=true]:border-emerald-700",
    },
    sky: {
        badge: "bg-sky-50 text-sky-800 border-sky-100",
        accent: "text-sky-700",
        button: "bg-sky-700 hover:bg-sky-800",
        chip: "border-sky-200 hover:border-sky-500 hover:bg-sky-50 data-[active=true]:bg-sky-700 data-[active=true]:text-white data-[active=true]:border-sky-700",
    },
    orange: {
        badge: "bg-orange-50 text-orange-800 border-orange-100",
        accent: "text-orange-700",
        button: "bg-orange-600 hover:bg-orange-700",
        chip: "border-orange-200 hover:border-orange-500 hover:bg-orange-50 data-[active=true]:bg-orange-600 data-[active=true]:text-white data-[active=true]:border-orange-600",
    },
};

export default function ServiceExplorer() {
    const { pillarKey } = useParams();
    const nav = useNavigate();
    const pillar = getPillar(pillarKey);
    const [service, setService] = useState(null);
    const [state, setState] = useState(null);

    if (!pillar) {
        return (
            <div className="min-h-screen bg-stone-50">
                <TopNav />
                <div className="max-w-3xl mx-auto p-10 text-center">
                    <h1 className="font-outfit text-2xl">Unknown service pillar</h1>
                    <Link to="/" className="text-emerald-700 hover:underline mt-4 inline-block">Back home</Link>
                </div>
            </div>
        );
    }

    const c = COLOR_MAP[pillar.color] || COLOR_MAP.emerald;

    function proceed() {
        const params = new URLSearchParams({
            mode: "register",
            role: pillar.primaryRole,
            pillar: pillar.key,
            service: service,
            state: state,
        });
        nav(`/auth?${params.toString()}`);
    }

    return (
        <div className="min-h-screen bg-stone-50">
            <TopNav />
            <main className="max-w-5xl mx-auto px-6 lg:px-8 py-10 animate-fade-in">
                <Link to="/" className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-800 mb-6">
                    <ArrowLeft className="w-4 h-4" /> Back to pillars
                </Link>

                <div data-testid={SERVICES_TESTIDS.pillarHeader} className="mb-10">
                    <Badge className={`${c.badge} rounded-full px-3 py-1 mb-3 hover:bg-transparent`}>
                        {pillar.eyebrow}
                    </Badge>
                    <h1 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-stone-900">
                        {pillar.title}
                    </h1>
                    <p className="mt-3 text-lg text-stone-600 font-dmsans max-w-2xl">{pillar.tagline}</p>
                </div>

                {/* Steps indicator */}
                <div className="flex items-center gap-3 mb-8 font-dmsans text-sm">
                    <div className={`flex items-center gap-2 ${service ? c.accent : "text-stone-900"} font-medium`}>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                            service ? "bg-emerald-100 text-emerald-800" : "bg-stone-900 text-white"
                        }`}>
                            {service ? <Check className="w-3.5 h-3.5" /> : "1"}
                        </div>
                        Pick a service
                    </div>
                    <div className="h-px w-8 bg-stone-300" />
                    <div className={`flex items-center gap-2 ${
                        !service ? "text-stone-400" : state ? c.accent : "text-stone-900"
                    } font-medium`}>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                            !service ? "bg-stone-100 text-stone-400" :
                            state ? "bg-emerald-100 text-emerald-800" : "bg-stone-900 text-white"
                        }`}>
                            {state ? <Check className="w-3.5 h-3.5" /> : "2"}
                        </div>
                        Pick a state
                    </div>
                    <div className="h-px w-8 bg-stone-300" />
                    <div className={`flex items-center gap-2 ${state ? "text-stone-900" : "text-stone-400"} font-medium`}>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                            state ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-400"
                        }`}>3</div>
                        Continue
                    </div>
                </div>

                {/* Step 1 */}
                <Card data-testid={SERVICES_TESTIDS.step1} className="rounded-3xl border-stone-200 mb-6">
                    <CardContent className="p-8">
                        <h2 className="font-outfit text-xl font-semibold text-stone-900 mb-4">
                            What service are you looking for?
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {pillar.services.map((s) => (
                                <button
                                    key={s.id}
                                    data-testid={SERVICES_TESTIDS.serviceOption}
                                    data-active={service === s.id}
                                    onClick={() => setService(s.id)}
                                    className={`text-left p-4 rounded-2xl border-2 transition-all font-dmsans text-sm font-medium ${c.chip}`}
                                >
                                    <div>{s.label}</div>
                                </button>
                            ))}
                        </div>
                    </CardContent>
                </Card>

                {/* Step 2 */}
                <Card data-testid={SERVICES_TESTIDS.step2} className={`rounded-3xl border-stone-200 mb-6 transition-opacity ${!service ? "opacity-40 pointer-events-none" : ""}`}>
                    <CardContent className="p-8">
                        <div className="flex items-center gap-2 mb-4">
                            <MapPin className={`w-5 h-5 ${c.accent}`} />
                            <h2 className="font-outfit text-xl font-semibold text-stone-900">
                                Where will services be rendered?
                            </h2>
                        </div>
                        <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2">
                            {US_STATES.map(([code, name]) => (
                                <button
                                    key={code}
                                    data-testid={SERVICES_TESTIDS.stateOption}
                                    data-code={code}
                                    data-active={state === code}
                                    onClick={() => setState(code)}
                                    title={name}
                                    className={`aspect-square rounded-xl border-2 transition-all font-outfit text-sm font-semibold ${c.chip}`}
                                >
                                    {code}
                                </button>
                            ))}
                        </div>
                        {state && (
                            <p className="mt-4 text-sm text-stone-600 font-dmsans">
                                Selected: <span className="font-medium text-stone-900">
                                    {US_STATES.find(([code]) => code === state)?.[1]}
                                </span>
                            </p>
                        )}
                    </CardContent>
                </Card>

                <div className="flex justify-end">
                    <Button
                        data-testid={SERVICES_TESTIDS.continueBtn}
                        onClick={proceed}
                        disabled={!service || !state}
                        className={`rounded-full text-white px-8 h-12 text-base ${c.button}`}
                    >
                        Continue <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                </div>
            </main>
        </div>
    );
}
